import { useAllSalebills } from "http-hooks/salebill/useSalebill";
import DeleteSaleBill from "components/saleBill/DeleteSaleBill";
import { useState } from "react";
import { toIndianCurrencyFormat } from "../utils/amount";
import { formatDateAndTime } from "utils/date";
import { useNavigate } from "react-router-dom";
import { SelectVendors, SelectCustomers } from "components/ui/selectors";
import { ActionButton } from "components/ui/Table";
import InputSearch from "components/ui/InputSearch";
import { getBillTypeWord } from "utils/salebill";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import useTableSort, { withSort } from "hooks/useTableSort";
import MasterPage, {
  FilterRow,
  FilterCell,
  ClearFiltersButton,
  AddButton,
  DeleteAction,
} from "components/ui/MasterPage";

const initialFilter = {
  salebillno: "",
  cusid: "",
  vendid: "",
  billtype: "VCN,VDN",
  page: 1,
  limit: 10,
};

const columns = [
  { key: "salebillno", header: "Note No.", sortable: true },
  {
    key: "salebilldate",
    header: "Date",
    sortable: true,
    render: (item) => formatDateAndTime(item.salebilldate),
  },
  {
    key: "billtype",
    header: "Type",
    sortable: true,
    render: (item) => (
      <span
        className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${
          item.billtype === "VCN"
            ? "bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20"
            : "bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20"
        }`}
      >
        {getBillTypeWord(item.billtype)}
      </span>
    ),
  },
  {
    key: "cndntype",
    header: "CN/DN Type",
    sortable: true,
    render: (item) => item.cndntype || "-",
  },
  {
    key: "customer",
    header: "Customer",
    sortable: true,
    render: (item) =>
      item.customer ? (
        <div className="font-medium">{item.customer.name}</div>
      ) : (
        item.cusid || "-"
      ),
  },
  {
    key: "vendor",
    header: "Vendor",
    sortable: true,
    render: (item) =>
      item.vendor ? (
        <div className="font-medium">{item.vendor.name}</div>
      ) : (
        item.vendid || "-"
      ),
  },
  {
    key: "salebillvalue",
    header: "Value",
    sortable: true,
    render: (item) => toIndianCurrencyFormat(item.salebillvalue),
  },
  { key: "actions", header: "Actions", isActions: true },
];

export default function CreditDebitNote() {
  const navigate = useNavigate();
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [searchNote, setSearchNote] = useState("");
  const [filter, setFilter] = useState(initialFilter);
  const tableSort = useTableSort();

  const handleCustomerChange = (customer) => {
    setSelectedCustomer(customer);
    setFilter((prev) => ({ ...prev, cusid: customer?.ps_Id || "", page: 1 }));
  };

  const handleVendorChange = (vendor) => {
    setSelectedVendor(vendor);
    setFilter((prev) => ({ ...prev, vendid: vendor?.ps_Id || "", page: 1 }));
  };

  const handleSearch = (searchTerm) => {
    setFilter((prev) => ({ ...prev, salebillno: searchTerm, page: 1 }));
  };

  const clearFilters = () => {
    setSelectedCustomer(null);
    setSelectedVendor(null);
    setSearchNote("");
    setFilter(initialFilter);
  };

  const {
    isLoading,
    data: { data: allNotes, page: currentPage, totalPages } = [],
  } = useAllSalebills(withSort(filter, tableSort));

  const handleEdit = (id) => {
    navigate(`/cndn/${parseInt(id)}`);
  };

  return (
    <MasterPage
      title="Credit / Debit Notes"
      headerActions={
        <AddButton onClick={() => navigate("/cndn/new")}>
          Add Credit/Debit Note
        </AddButton>
      }
      filters={
        <FilterRow>
          <FilterCell>
            <InputSearch
              value={searchNote}
              onChange={(e) => setSearchNote(e.target.value)}
              onSearch={handleSearch}
              label="Note No."
              placeholder="Search by note number"
            />
          </FilterCell>
          <FilterCell>
            <SelectCustomers
              value={selectedCustomer}
              onChange={handleCustomerChange}
              label="Customer"
              placeholder="Select Customer"
            />
          </FilterCell>
          <FilterCell>
            <SelectVendors
              value={selectedVendor}
              onChange={handleVendorChange}
              label="Vendor"
              placeholder="Select Vendor"
            />
          </FilterCell>
          <ClearFiltersButton onClick={clearFilters} />
        </FilterRow>
      }
      columns={columns}
      sort={tableSort}
      data={allNotes}
      isLoading={isLoading}
      rowKey="salebillid"
      deleteComponent={DeleteSaleBill}
      renderActions={(note, setDeleteItemId) => (
        <>
          <ActionButton
            variant="secondary"
            onClick={() => handleEdit(note.salebillid)}
            title="Edit"
          >
            <PencilSquareIcon className="h-4 w-4" />
          </ActionButton>
          <DeleteAction
            onClick={() => setDeleteItemId(note.salebillid)}
            title="Delete"
          />
        </>
      )}
      pagination={{
        currentPage,
        totalPages,
        onPageChange: (page) => setFilter((prev) => ({ ...prev, page })),
      }}
    />
  );
}
